/**
 * Katalog-Spalten am Mangel — `inspection_items.katalog_key` und
 * `katalog_kategorie` (SQL Nr. 131, supabase/inspection_items_katalog.sql,
 * Fassung 2: keine Nummer, kein Quellenbezug — Entscheid 28.09.).
 *
 * Plan: docs/mangelkatalog-plan.md („Datenmodell und Speicherung“) ·
 * Vertrag: docs/mangelkatalog-vertrag.md (Paket E).
 *
 * ─── Schreiben: nur definierte Felder ──────────────────────────────────────
 * `save_project_children_tx` baut seine Spaltenliste aus `jsonb_object_keys`:
 * ein FEHLENDER Schlüssel wird nie aktualisiert. Deshalb steht ein Schlüssel
 * nur in der Zeile, wenn das Feld am Eintrag definiert ist — eine Ansicht
 * oder alte Sitzung ohne die Felder lässt den Bestand stehen. `null` heißt
 * „bewusst gelöscht“ und wird durchgereicht (Muster Verzicht in
 * cloudService.ts). Dieselbe RPC schreibt nur Spalten, die es gibt: ohne
 * Nr. 131 fallen die Schlüssel still weg (fail-open). Andere Felder am
 * Eintrag (auch Altstände aus der ersten Fassung) werden nie geschrieben.
 *
 * ─── Längen wie der CHECK ─────────────────────────────────────────────────
 * `inspection_items_katalog_chk` weist zu lange Werte ab — und ein CHECK
 * bricht das GANZE Projekt-Speichern ab, nicht nur den einen Mangel. Deshalb
 * wird hier vorher geprüft: zu lang (oder kein String) → `null`. Gezählt wird
 * in UTF-16-Einheiten (`.length`); das ist nie weniger als die Zeichenzahl,
 * die Postgres mit `length()` zählt — die Prüfung hier ist also höchstens
 * strenger als der CHECK, nie lockerer.
 */

/** Höchstlängen je Spalte — identisch mit `inspection_items_katalog_chk` (Nr. 131). */
export const KATALOG_GRENZEN = { katalog_key: 120, katalog_kategorie: 120 } as const;

export type KatalogSpalte = keyof typeof KATALOG_GRENZEN;

/** Die beiden Spaltennamen, z. B. für den Rückfall ohne Nr. 131. */
export const KATALOG_SPALTEN: readonly KatalogSpalte[] = Object.freeze([
    'katalog_key', 'katalog_kategorie',
]);

/** Die Katalog-Felder am Eintrag (`InspectionItem`). */
export interface KatalogFelder {
    katalogKey?: string | null;
    katalogKategorie?: string | null;
}

/** Der Katalog-Teil einer DB-Zeile — nur die Schlüssel, die geschrieben werden sollen. */
export type KatalogSpaltenZeile = Partial<Record<KatalogSpalte, string | null>>;

const FELD_ZU_SPALTE: ReadonlyArray<readonly [keyof KatalogFelder, KatalogSpalte]> = [
    ['katalogKey', 'katalog_key'],
    ['katalogKategorie', 'katalog_kategorie'],
];

const KATALOG_SPALTEN_MENGE: ReadonlySet<string> = new Set(KATALOG_SPALTEN);

/** Getrimmter String innerhalb der Grenze; leer, zu lang oder kein String → null. */
const spaltenWert = (wert: unknown, grenze: number): string | null => {
    if (typeof wert !== 'string') return null;
    const getrimmt = wert.trim();
    if (getrimmt === '' || getrimmt.length > grenze) return null;
    return getrimmt;
};

/**
 * Baut den Katalog-Teil einer `inspection_items`-Zeile.
 *
 * NUR die Schlüssel, deren Feld `!== undefined` ist. Wert: getrimmter String
 * innerhalb von `KATALOG_GRENZEN`, sonst `null` (auch `null` bleibt `null`).
 */
export const katalogSpaltenSchreiben = (item: KatalogFelder): KatalogSpaltenZeile => {
    const zeile: KatalogSpaltenZeile = {};
    for (const [feld, spalte] of FELD_ZU_SPALTE) {
        const wert: unknown = item[feld];
        if (wert === undefined) continue;
        zeile[spalte] = spaltenWert(wert, KATALOG_GRENZEN[spalte]);
    }
    return zeile;
};

/**
 * Liest die Katalog-Felder aus einer DB-Zeile (`select('*')`). Nur Strings
 * werden übernommen; `null` oder eine fehlende Spalte (Nr. 131 noch nicht
 * gelaufen) lässt den Schlüssel weg — der nächste Speichervorgang schreibt
 * ihn dann auch nicht.
 */
export const katalogFelderLesen = (dbRow: Record<string, unknown>): Partial<Record<keyof KatalogFelder, string>> => {
    const felder: Partial<Record<keyof KatalogFelder, string>> = {};
    for (const [feld, spalte] of FELD_ZU_SPALTE) {
        const wert = dbRow[spalte];
        if (typeof wert === 'string') felder[feld] = wert;
    }
    return felder;
};

/** Trägt die Zeile mindestens einen Katalog-Schlüssel? */
export const hatKatalogSpalten = (zeile: object): boolean =>
    Object.keys(zeile).some((schluessel) => KATALOG_SPALTEN_MENGE.has(schluessel));

/** Kopie der Zeile ohne die Katalog-Schlüssel (Rückfall ohne Nr. 131). */
export const ohneKatalogSpalten = <T extends object>(zeile: T): Omit<T, KatalogSpalte> =>
    Object.fromEntries(
        Object.entries(zeile).filter(([schluessel]) => !KATALOG_SPALTEN_MENGE.has(schluessel)),
    ) as Omit<T, KatalogSpalte>;

/**
 * Meldet PostgREST/Postgres eine unbekannte Spalte? `PGRST204` („Could not
 * find the '…' column … in the schema cache“) beim Direkt-Upsert, `42703`
 * (undefined_column) aus Postgres.
 */
export const istUnbekannteSpalteFehler = (fehler: unknown): boolean => {
    if (typeof fehler !== 'object' || fehler === null || !('code' in fehler)) return false;
    return fehler.code === '42703' || fehler.code === 'PGRST204';
};
